import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useLocation, useNavigate } from 'react-router';

import { GroupedDropdown } from '@src/components/GroupedDropdown';
import { Loader } from '@src/components/ui';
import { parseSearch, stringifySearch } from '@src/utils/urlHelpers';

import {
    isProductNewsGroupedDropdownDataLoading,
    getProductNewsGroupedDropdownData,
} from './selectors';

const ProductNewsAndUpdatesFilter = ({
    productsData,
    isCategoriesLoading,
    categories,
    platforms,
    isPlatformsLoading,
}) => {
    const location = useLocation();
    const navigate = useNavigate();

    const dropdownData = useCallback(() => {
        return getProductNewsGroupedDropdownData(productsData, isCategoriesLoading, categories, platforms);
    }, [productsData, isCategoriesLoading, categories, platforms]);

    const handleDropdownChange = useCallback((targetId, selectedType) => {
        const queryParams = parseSearch(location.search);
        if (targetId && targetId !== GroupedDropdown.ALL_OPTION) {
            queryParams.option = targetId;
            queryParams.type = selectedType;
        } else {
            delete queryParams.option;
            delete queryParams.type;
        }

        delete queryParams.page;

        navigate({ search: stringifySearch(queryParams) });
    }, [location.search, navigate]);

    const renderLayout = useCallback((child) => (
        <Row className="justify-content-center">
            <Col sm={12} md={4} className="mb-3">
                {child}
            </Col>
        </Row>
    ), []);

    if (isProductNewsGroupedDropdownDataLoading(productsData, isCategoriesLoading, isPlatformsLoading)) {
        return renderLayout(<Loader />);
    }

    const selectedOption = parseSearch(location.search).option;

    return renderLayout(
        <GroupedDropdown
            defaultSelection="All"
            groupedList={dropdownData()}
            selectedOption={selectedOption}
            title="Jump to Filter"
            onChange={handleDropdownChange}
        />,
    );
};

ProductNewsAndUpdatesFilter.propTypes = {
    isCategoriesLoading: PropTypes.bool,
    categories: PropTypes.arrayOf(PropTypes.shape({
        slug: PropTypes.string.isRequired,
    })).isRequired,
    currentCategory: PropTypes.shape({
        id: PropTypes.string.isRequired, // slug
        title: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    }),
    productsData: ImmutablePropTypes.mapContains({
        loaded: PropTypes.string.isRequired,
        products: ImmutablePropTypes.listOf(ImmutablePropTypes.mapContains({
            id: PropTypes.number,
        })),
        total: PropTypes.number,
        page: PropTypes.number,
    }).isRequired,
    platforms: PropTypes.arrayOf(PropTypes.shape({
        slug: PropTypes.string.isRequired,
        name: PropTypes.string,
    })),
    isPlatformsLoading: PropTypes.bool,
};

export default React.memo(ProductNewsAndUpdatesFilter);
